import { Fragment, useEffect, useState } from 'react';
import { t } from '@lingui/core/macro';
import { Alert, LoaderOverlay, Box } from '@pocketlaw/tetris';
import { useHistory } from 'react-router-dom';
import { MainContent } from 'app/domains/common-ui';
import { useACLCan } from 'app/domains/filesystem';
import { Pagination } from 'shared/domains/common-ui';
import { ScreenLayout } from 'shared/domains/common-ui/ScreenLayout';
import { AccessInformation } from './AccessInformation';
import Dialog from './Dialog';
import { DropUpload } from './DropUpload';
import { FileSystemEmptyFolder } from './FileSystemEmptyFolder';
import { useActiveWorkspace, FileSystemProvider } from './FileSystemProvider';
import FileSystemTable from './FileSystemTable';
import { Header } from './Header';
import useCacheHandlers from './useCacheHandlers';
import useDialogState from './useDialogState';
import { useFileSystemEntries } from './useFileSystemEntries';
import useSortState from './useSortState';
import { AlertContainer } from './styled';
export { FileSystemProvider, useActiveWorkspace };
const PAGE_SIZE = 50;
export function FileSystem() {
    var _a, _b;
    const activeWorkspace = useActiveWorkspace();
    const [entriesOffset, setEntriesOffset] = useState(0);
    const { folderId, fileSystemData, loading, error } = activeWorkspace;
    const { folder } = fileSystemData || {};
    const history = useHistory();
    const [state, { sort }] = useSortState();
    const entriesQuery = useFileSystemEntries(Object.assign(Object.assign({}, state), { folderId, limit: PAGE_SIZE, offset: entriesOffset }));
    useEffect(() => {
        setEntriesOffset(0);
    }, [folderId]);
    const [dialog, actions] = useDialogState();
    const { createEntries, renameEntries, deleteEntries } = useCacheHandlers(entriesQuery.updateEntries);
    const { canWrite, accessLevel } = useACLCan({ userAcls: folder === null || folder === void 0 ? void 0 : folder.acl, teamAcls: folder === null || folder === void 0 ? void 0 : folder.teamAcls });
    const { entries = [], count } = (_a = entriesQuery.data) !== null && _a !== void 0 ? _a : {};
    const enableDropZone = canWrite();
    const hasError = error || entriesQuery.error;
    const handleDuplicated = (createdDraftId) => {
        history.push(`/draft/${createdDraftId}`);
    };
    const handleDocumentsUploaded = (entryIds, locationFolderId) => {
        if (folderId === locationFolderId) {
            entriesQuery.refetch();
        }
        else if (!locationFolderId) {
            history.push('/files');
        }
        else {
            history.push(`/files/${locationFolderId}`);
        }
    };
    if (hasError) {
        return (<AlertContainer>
        <Alert appearance="danger" title={t({
                message: 'Could not load workspace',
                comment: 'Alert title displayed when we could not load the workspace',
            })} message={t({
                message: 'Something went wrong while loading the workspace, please try again later',
                comment: 'Alert message displayed when we could not load the workspace',
            })}/>
      </AlertContainer>);
    }
    return (<Fragment>
      <ScreenLayout.Root id="repository" topbar={<Header onUploadFolder={actions.uploadFolder} onUploadFile={actions.uploadFiles} onCreateFolder={actions.createFolder}/>} content={<MainContent>
            <AccessInformation accessLevel={accessLevel}/>

            {enableDropZone && <DropUpload onDropUpload={actions.uploadFiles}/>}

            <Box justifyContent="flex-end" mb="md">
              <Pagination disabled={entriesQuery.loading} limit={PAGE_SIZE} count={count} offset={entriesOffset} setOffset={setEntriesOffset}/>
            </Box>

            {Boolean(!entries.length && !loading) && ((_b = entriesQuery.data) === null || _b === void 0 ? void 0 : _b.folder) && (<FileSystemEmptyFolder from={entriesQuery.data.folder} onUploadFolder={actions.uploadFolder} onUploadFile={actions.uploadFiles} onCreateFolder={actions.createFolder}/>)}

            <LoaderOverlay enabled={entriesQuery.loading} transparent>
              {Boolean(entries.length) && (<FileSystemTable {...state} entries={entries} onSort={sort} onDuplicate={actions.duplicate} onRename={actions.rename} onMove={actions.move} onQuickMove={actions.quickMove} onDelete={actions.delete}/>)}
            </LoaderOverlay>
          </MainContent>}/>

      <Dialog action={dialog} folder={folder} onCreated={createEntries} onDuplicated={handleDuplicated} onRenamed={renameEntries} onDeleted={deleteEntries} onDocumentsUploaded={handleDocumentsUploaded} onClose={actions.reset}/>
    </Fragment>);
}
